/* global FileReader, Worker */
import React, { useState, useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Container, Alert,
} from '@mui/material';
import Papa from 'papaparse';

import FallbackOnError from '../components/FallbackOnError';
import UploadPanel from '../components/hashing/UploadPanel';
import ColumnMappingDialog from '../components/hashing/ColumnMappingDialog';
import ResultsPanel from '../components/hashing/ResultsPanel';
import { autoDetectColumn } from '../utils/hashingUtils';
import { logReactErrBoundaryError } from '../utils';
import logger from '../utils/logger';

function readCsvHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      Papa.parse(e.target.result, {
        header: true,
        preview: 1,
        complete: (results) => resolve(results.meta.fields || []),
        error: (err) => reject(new Error(err.message)),
      });
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file.slice(0, 1024 * 1024));
  });
}

function buildColumnMapping(headerData, dataTypeValue) {
  const mapping = {};
  let needsDialog = false;

  headerData.forEach(({ name, headers }) => {
    const detected = autoDetectColumn(headers, dataTypeValue);
    if (detected) {
      mapping[name] = detected;
    } else if (headers.length === 1) {
      [mapping[name]] = headers;
    } else {
      mapping[name] = '';
      needsDialog = true;
    }
  });

  return { mapping, needsDialog };
}

export { buildColumnMapping };

function HashingPage() {
  useEffect(() => { document.title = 'MissionPortal | Hashing'; }, []);

  const [phase, setPhase] = useState('upload');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [dataType, setDataType] = useState(null);
  const [normalizePhone, setNormalizePhone] = useState(false);
  const [normalizeEmail, setNormalizeEmail] = useState(false);
  const [exportMode, setExportMode] = useState('merge');
  const [fileHeaders, setFileHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [results, setResults] = useState(null);
  const [workerError, setWorkerError] = useState(null);

  const handleFilesChange = useCallback(async (nextFiles, err) => {
    setFiles(nextFiles);
    setFileError(err);
    setWorkerError(null);

    if (!err && nextFiles.length > 0) {
      const headerResults = await Promise.all(
        nextFiles.map(async (f) => {
          try {
            const headers = await readCsvHeaders(f);
            return { name: f.name, headers };
          } catch (e) {
            logger.error('Error reading CSV headers', e);
            return { name: f.name, headers: null };
          }
        }),
      );

      const failedFiles = headerResults.filter((h) => h.headers === null);
      if (failedFiles.length > 0) {
        setFileError(`Could not read column headers from: ${failedFiles.map((h) => h.name).join(', ')}`);
        setFileHeaders([]);
        setColumnMapping({});
        setShowDialog(false);
        return;
      }

      const headerData = headerResults;
      setFileHeaders(headerData);

      const { mapping, needsDialog } = buildColumnMapping(headerData, dataType);
      setColumnMapping(mapping);
      setShowDialog(needsDialog);
    } else {
      setFileHeaders([]);
      setColumnMapping({});
      setShowDialog(false);
    }
  }, [dataType]);

  const handleDataTypeChange = useCallback((newDataType) => {
    setDataType(newDataType);

    if (fileHeaders.length > 0) {
      const { mapping, needsDialog } = buildColumnMapping(fileHeaders, newDataType);
      setColumnMapping(mapping);
      setShowDialog(needsDialog);
    }
  }, [fileHeaders]);

  const triggerDownload = useCallback((outputs) => {
    outputs.forEach((output) => {
      const blob = new Blob([output.csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = output.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }, []);

  const handleHashClick = useCallback(async () => {
    setProcessing(true);
    setProgress(0);
    setWorkerError(null);

    try {
      const worker = new Worker(
        new URL('../workers/hashingWorker.js', import.meta.url),
        { type: 'module' },
      );

      worker.onmessage = ({ data }) => {
        if (data.type === 'PROGRESS') {
          setProgress(data.payload.pct);
          setCurrentFile(data.payload.currentFile);
        } else if (data.type === 'DONE') {
          setResults(data.payload);
          setProcessing(false);
          setPhase('results');
          worker.terminate();
        } else if (data.type === 'ERROR') {
          setWorkerError(data.payload.message);
          setProcessing(false);
          worker.terminate();
        }
      };

      worker.onerror = (e) => {
        setWorkerError(e.message || 'Worker error while hashing.');
        setProcessing(false);
        worker.terminate();
      };

      worker.onmessageerror = () => {
        setWorkerError('Worker message error while hashing.');
        setProcessing(false);
        worker.terminate();
      };

      worker.postMessage({
        type: 'PROCESS',
        payload: {
          files,
          columnMapping,
          dataType,
          normalizePhone: dataType === 'phone' ? normalizePhone : false,
          normalizeEmail: dataType === 'email' ? normalizeEmail : false,
          exportMode,
        },
      });
    } catch (err) {
      setWorkerError(err.message);
      setProcessing(false);
    }
  }, [files, columnMapping, dataType, normalizePhone, normalizeEmail, exportMode]);

  const handleReset = useCallback(() => {
    setPhase('upload');
    setFiles([]);
    setFileError(null);
    setFileHeaders([]);
    setColumnMapping({});
    setShowDialog(false);
    setNormalizePhone(false);
    setNormalizeEmail(false);
    setProcessing(false);
    setProgress(0);
    setCurrentFile(null);
    setResults(null);
    setWorkerError(null);
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={FallbackOnError}
      onError={logReactErrBoundaryError}
      onReset={(details) => { logger.info('ErrorBoundary reset', details); }}
    >
      <header className="App-header">
        <h2>
          Hashing Tool
        </h2>
      </header>
      <Container maxWidth="md" sx={{ py: 4 }}>

        {workerError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWorkerError(null)}>
            {workerError}
          </Alert>
        )}

        {phase === 'upload' && (
          <>
            <UploadPanel
              files={files}
              onFilesChange={handleFilesChange}
              fileError={fileError}
              dataType={dataType}
              onDataTypeChange={handleDataTypeChange}
              normalizePhone={normalizePhone}
              onNormalizePhoneChange={setNormalizePhone}
              normalizeEmail={normalizeEmail}
              onNormalizeEmailChange={setNormalizeEmail}
              exportMode={exportMode}
              onExportModeChange={setExportMode}
              columnMapping={columnMapping}
              onHashClick={handleHashClick}
              processing={processing}
              progress={progress}
              currentFile={currentFile}
            />
            <ColumnMappingDialog
              open={showDialog}
              fileHeaders={fileHeaders}
              columnMapping={columnMapping}
              onMappingChange={(filename, col) => setColumnMapping(
                (prev) => ({ ...prev, [filename]: col }),
              )}
              onConfirm={() => setShowDialog(false)}
              onCancel={() => { setShowDialog(false); handleReset(); }}
            />
          </>
        )}

        {phase === 'results' && results && (
          <ResultsPanel
            results={results}
            onDownload={triggerDownload}
            onReset={handleReset}
          />
        )}
      </Container>
    </ErrorBoundary>
  );
}

export default HashingPage;
